/** @jsx jsx */
import React, { useEffect } from 'react';
import { jsx } from '@emotion/core';
import ActionButtonsContainer from 'components/ActionButtonsContainer';

import useTranslate, {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';

import useWorkflow from 'hooks/useWorkflow';

import styles from '../styles';
import { Divider } from '@material-ui/core';
import ExpandablePanel from 'components/ExpandablePanel';
import Button from 'components/Button';
import env from 'utils/env';
import qs from 'qs';
import { useSecureQueryParameters } from 'hooks/useQueryParameters';

import useAnalytics, { Event } from 'hooks/useAnalytics';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import BreadTitle from 'features/unified/OfferIntro/BreadTitle';

interface CreditCardFallbackProps {
  panelName: string;
  expanded: boolean;
  handlePanelExpandChange: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
}

const CreditCardFallback: React.FC<CreditCardFallbackProps> = props => {
  const { panelName, expanded, handlePanelExpandChange } = props;
  const { restartPath } = useWorkflow();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const t = useTranslate();
  const allowedQueryParameters = useSecureQueryParameters();

  const { sendEvents } = useAnalytics();
  const { setAnalyticsVariables } = useAnalyticVariables();

  useEffect(() => {
    expanded && sendEvents([Event.PrequalFallbackExpanded]);
  }, [setAnalyticsVariables, expanded, sendEvents]);

  const fullRestartPath = `${env.BASENAME}/${restartPath}${qs.stringify(
    allowedQueryParameters,
    {
      addQueryPrefix: true,
    },
  )}`.replace(/\/\//g, '/');

  const title = (
    <div>
      <div css={styles.paymentOption.summaryTitle}>
        <BreadTitle
          id={'creditCardFallbackBreadLogo'}
          titlePart={
            <React.Fragment>
              {tMd('unified.offer.paymentOption.creditCard.fallback.title')}
              {t('unified.creditCardLogo') !== '' && (
                <img
                  src={`data:image/png;base64,${t('unified.creditCardLogo')}`}
                  alt={t('unified.creditCardLogoAlt')}
                  css={styles.paymentOption.cardIcon}
                />
              )}
            </React.Fragment>
          }
          logo={t('unified.offer.paymentOption.creditCard.fallback.breadLogo')}
        />
      </div>
      <div css={styles.paymentOption.summaryBody}>
        {tMdMultiline(
          'unified.offer.paymentOption.creditCard.fallback.description',
        )}
      </div>
    </div>
  );

  const body = (
    <div css={styles.paymentOption.body}>
      <div css={styles.paymentOption.section}>
        <Divider />
      </div>
      <div css={styles.paymentOption.section}>
        {tMd('unified.offer.paymentOption.creditCard.fallback.message')}
      </div>
    </div>
  );

  const footer = (
    <React.Fragment>
      <ActionButtonsContainer>
        <Button component="a" href={fullRestartPath}>
          {t('unified.offer.paymentOption.creditCard.fallback.restartButton')}
        </Button>
      </ActionButtonsContainer>
    </React.Fragment>
  );
  return (
    <ExpandablePanel
      title={title}
      children={body}
      footer={footer}
      panelName={panelName}
      expanded={expanded}
      onChange={handlePanelExpandChange}
      banner={tMd('unified.offer.paymentOption.creditCard.fallback.banner')}
    />
  );
};

export default CreditCardFallback;
